<template>
  <div>
    <div>
      Do veljavnosti dokumenta vas loči le še e-podpis!<br /><br />Vnesite imena in email naslove podpisnikov in
      dobili bodo elektronski zahtevek za podpis dokumenta.
      <br />Podpisniki s telefonsko številko bodo prejeli tudi SMS s povezavo za podpis.
    </div>
    <div class="e-signature__signer-suggestions" v-if="signerSuggestions.length">
      <div class="e-signature__signers-title">
        Pogosti podpisniki
      </div>
      <div class="e-signature__signer e-signature__signer-suggestion" v-for="(signer, index) in signerSuggestions" :key="index">
        <v-field class="md-field--dark">
          <v-input
            class="md-input--dark"
            v-model="signer.name"
            disabled
          />
        </v-field>
        <v-field class="md-field--dark">
          <v-input
            class="md-input--dark"
            v-model="signer.email"
            disabled
          />
        </v-field>
        <v-field class="md-field--dark">
          <v-input
            class="md-input--dark"
            :value="signer.phone"
            placeholder="Telefonska številka"
            disabled
          />
        </v-field>
        <v-button class="e-signature__signer-suggestion-button" size="medium" outlined @click="addSignerSuggestion(signer)">
          + Dodaj med podpisnike
        </v-button>
        <div @click="addSignerSuggestion(signer)">
          <v-icon class="e-signature__signer-icon e-signature__signer-suggestion-small-button">
            add
          </v-icon>
          <v-tooltip>Dodaj med podpisnike</v-tooltip>
        </div>
      </div>
    </div>
    <div>
      <div class="e-signature__signers-title">
        Podpisniki
      </div>
      <div class="e-signature__signer" v-for="(signer, index) in form.signers" :key="index">
        <v-field class="md-field--dark" :class="geFieldValidationClass(index, 'name')">
          <v-input
            class="md-input--dark"
            v-model="signer.name"
            placeholder="Ime in priimek"
            :key="`name-${index}`"
            ref="nameInputs"
            @keydown.enter="$emit('sign')"
          />
          <span class="md-error" v-if="$v.form.signers.$each[index].name.$invalid">Vpišite ime in priimek</span>
        </v-field>
        <v-field class="md-field--dark" :class="geFieldValidationClass(index, 'email')">
          <v-input
            class="md-input--dark"
            v-model="signer.email"
            placeholder="Email naslov"
            :key="`email-${index}`"
            @keydown.enter="$emit('sign')"
          />
          <span class="md-error" v-if="$v.form.signers.$each[index].email.$invalid">Vpišite veljaven email naslov</span>
        </v-field>
        <v-field class="md-field--dark" :class="geFieldValidationClass(index, 'phone')">
          <v-input
            class="md-input--dark"
            v-model="signer.phone"
            placeholder="Telefonska številka (neobvezno)"
            :key="`phone-${index}`"
            @keydown.enter="$emit('sign')"
          />
          <span class="md-error" v-if="signer.phone && $v.form.signers.$each[index].phone.$invalid">Vpišite veljavno telefonsko številko</span>
        </v-field>
        <div @click="removeSigner(index)">
          <v-icon class="e-signature__signer-icon">
            delete
          </v-icon>
          <v-tooltip>Odstrani podpisnika</v-tooltip>
        </div>
      </div>
      <div class="e-signature__content__add-signer-button">
        <v-button size="medium" outlined @click="addSigner">
          + Dodaj podpisnika
        </v-button>
      </div>
      <div class="e-signature__order">
        <v-checkbox v-model="form.withOrder">Upoštevaj vrstni red podpisnikov</v-checkbox>
        <div class="e-signature__order-help">
          <icon class="help" name="question-mark" :width="18" :height="18" />
          <v-tooltip>Če označite to polje, bo vrstni red podpisnikov določen glede na zaporedje podpisnikov zgoraj.</v-tooltip>
        </div>
      </div>
      <div class="e-signature__content__add-signer-button">
        <v-button size="medium" :disabled="isSigningDisabled" @click="$emit('sign')">
          Pošlji v E-podpis
        </v-button>
        <v-tooltip v-if="isSigningDisabled" md-direction="top">
          Za pošiljanje v E-podpis je potrebno dodati vsaj enega podpisnika
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { required, minLength, email } from 'vuelidate/lib/validators'
import Icon from 'design-system/icons/Icon.vue'
import ValidationMixin from '@/components/shared/ValidationMixin'
import VCheckbox from 'design-system/VCheckbox'
import VField from 'design-system/VField.vue'
import VIcon from 'design-system/VIcon.vue'
import VInput from 'design-system/VInput.vue'
import VButton from 'design-system/VButton.vue'
import VTooltip from 'design-system/VTooltip.vue'
import { mapState } from 'vuex'

function normalizePhone (value) {
  if (!value) {
    return null
  }
  let compact = String(value).replace(/[\s.\-()]/g, '')
  if (!compact) {
    return null
  }
  if (compact.startsWith('00')) {
    compact = `+${compact.slice(2)}`
  } else if (compact.startsWith('0')) {
    compact = `+386${compact.slice(1)}`
  } else if (!compact.startsWith('+') && compact.startsWith('386')) {
    compact = `+${compact}`
  } else if (!compact.startsWith('+')) {
    return null
  }
  return /^\+[1-9]\d{7,14}$/.test(compact) ? compact : null
}

export default {
  components: {
    Icon,
    VCheckbox,
    VField,
    VIcon,
    VInput,
    VButton,
    VTooltip,
  },
  props: {
    signers: {
      type: Array,
      required: true,
    },
    frequentSigners: {
      type: Array,
      default: () => [],
    },
  },
  mixins: [ValidationMixin],
  created () {
    if (!this.signers) {
      return
    }
    this.form.signers = this.signers.map(signer => ({
      name: signer.name || '',
      email: signer.email || '',
      phone: signer.phone || '',
    }))
  },
  data () {
    return {
      form: {
        signers: [{ name: '', email: '', phone: '' }],
        withOrder: false,
        isInitialized: false,
      },
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (!this.$refs.nameInputs[0]) {
        return
      }
      this.$refs.nameInputs[0].$el.focus()
    })
  },
  computed: {
    ...mapState(['isLoading']),
    signerSuggestions () {
      return this.frequentSigners.filter(s => !this.form.signers.some(signer => signer.email === s.email))
    },
    isSigningDisabled () {
      return this.isLoading || !this.isValid()
    },
  },
  methods: {
    addSigner () {
      this.form.signers.push({ name: '', email: '', phone: '' })
    },
    removeSigner (index) {
      this.form.signers.splice(index, 1)
    },
    addSignerSuggestion (signer) {
      this.form.signers.push({ name: signer.name, email: signer.email, phone: signer.phone || '' })
    },
    isSignerPropertyForIndexEmpty (index, propertyName) {
      return this.form.signers[index][propertyName] === ''
    },
    geFieldValidationClass (index, propertyName) {
      if (this.isSignerPropertyForIndexEmpty(index, propertyName)) {
        return
      }

      const signer = this.$v.form.signers.$each[index]
      if (signer) {
        const fieldValue = signer[propertyName]
        return {
          'md-invalid': fieldValue.$invalid,
        }
      }
    },
    runAllValidations () {
      this.$v.$touch()
    },
    isValid () {
      return !this.$v.$invalid
    },
  },
  watch: {
    form: {
      handler (newFormValue) {
        this.$emit('change', {
          ...this.signers,
          ...newFormValue,
        })
      },
      deep: true,
    },
    signerSuggestions (newSignerSuggestions) {
      if (!this.form.isInitialized && newSignerSuggestions.length > 0 && this.form.signers.length === 1 && this.form.signers[0].name === '' && this.form.signers[0].email === '') {
        this.form.signers = []
        this.form.isInitialized = true
      }
    },
  },
  validations: {
    form: {
      signers: {
        required,
        minLength: minLength(1),
        $each: {
          name: {
            required,
          },
          email: {
            required,
            email,
          },
          phone: {
            valid (value) {
              if (!value || value.trim() === '') {
                return true
              }
              return normalizePhone(value) !== null
            },
          },
        },
      },
    },
  },
}
</script>

<style lang="scss" scoped>
@import "app/assets/stylesheets/variables";

.e-signature__signer {
  display: flex;
  align-items: center;

  @include respond-to(not-large) {
    display: block;
    max-width: 500px;
    margin-bottom: 24px;
  }

  > *:not(:last-child) {
    margin-right: 24px;
  }

  .md-field {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
    padding-top: 0;
  }

  @include respond-to(small) {
    flex-direction: column;
    align-items: flex-start;

    > *:not(:last-child) {
      margin-right: 0;
      margin-bottom: 16px;
    }
  }
}

.e-signature__signer-suggestions {
  margin-bottom: 24px;
}

.e-signature__signers-title {
  font-weight: 500;
  margin: 32px 0 8px 0;
}

.e-signature__signer-suggestion {
  margin-bottom: 4px;

  .md-input {
    opacity: 0.5;
  }
}

.e-signature__signer-suggestion-small-button {
  display: none;

  @include respond-to(medium) {
    display: block;
  }
}

.e-signature__signer-suggestion-button {
  display: none;

  @include respond-to(small) {
    display: block;
  }
}

.e-signature__signer-icon {
  cursor: pointer;

  &--disabled {
    cursor: not-allowed;
  }
}

.e-signature__content__add-signer-button {
  width: fit-content;
  margin-top: 24px;
}

.e-signature__order {
  display: flex;
  align-items: center;
  width: fit-content;
}
</style>
